import { defineStore } from 'pinia';
import { config } from '@/config';

export const useAnalysisStore = defineStore('analysis', {
  state: () => ({
    analyses: [],
    currentAnalysis: null,
    loading: false,
    error: null,
    totalAnalyses: 0,
    currentPage: 1,
    limit: 15,
    pollingIntervals: new Map(), // Map of analysisId -> intervalId
  }),

  actions: {
    /**
     * Fetch all analyses for a user with pagination
     * @param {string} userId - Firebase UID
     * @param {object} options - Query options (status, limit, offset)
     */
    async fetchUserAnalyses(userId, options = {}) {
      this.loading = true;
      this.error = null;

      try {
        const { status, limit = this.limit, offset = 0 } = options;
        
        let url = `${config.API_BASE_URL}/analyses/user/${userId}?limit=${limit}&offset=${offset}`;
        
        if (status) {
          url += `&status=${status}`;
        }

        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to fetch analyses');
        }

        const data = await response.json();
        this.analyses = data.analyses;
        this.totalAnalyses = data.total;
        this.loading = false;

        return data;
      } catch (error) {
        this.error = error.message;
        this.loading = false;
        console.error('Error fetching analyses:', error);
        throw error;
      }
    },

    /**
     * Create a new analysis
     * @param {object} analysisData - Analysis creation data
     */
    async createAnalysis(analysisData) {
      this.loading = true;
      this.error = null;

      try {
        const response = await fetch(`${config.API_BASE_URL}/analyses`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(analysisData),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to create analysis');
        }

        const data = await response.json();
        this.loading = false;

        // Refresh the analyses list
        await this.fetchUserAnalyses(analysisData.user_id, {
          limit: this.limit,
          offset: (this.currentPage - 1) * this.limit,
        });

        return data;
      } catch (error) {
        this.error = error.message;
        this.loading = false;
        console.error('Error creating analysis:', error);
        throw error;
      }
    },

    /**
     * Fetch a single analysis by ID
     * @param {string} analysisId - Analysis UUID
     * @param {string} userId - User ID for authorization
     */
    async fetchAnalysisById(analysisId, userId) {
      this.loading = true;
      this.error = null;

      try {
        const response = await fetch(
          `${config.API_BASE_URL}/analyses/${analysisId}?user_id=${userId}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'X-User-Id': userId,
            },
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to fetch analysis');
        }

        const data = await response.json();
        this.currentAnalysis = data;

        // Update the analysis in the list if it exists
        const index = this.analyses.findIndex(
          (a) => a.analysis_id === analysisId
        );
        if (index !== -1) {
          this.analyses[index] = data;
        }

        this.loading = false;

        return data;
      } catch (error) {
        this.error = error.message;
        this.loading = false;
        console.error('Error fetching analysis:', error);
        throw error;
      }
    },

    /**
     * Poll analysis status (lightweight endpoint)
     * @param {string} analysisId - Analysis UUID
     * @param {string} userId - User ID for authorization
     */
    async pollAnalysisStatus(analysisId, userId) {
      try {
        const response = await fetch(
          `${config.API_BASE_URL}/analyses/${analysisId}/status?user_id=${userId}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'X-User-Id': userId,
            },
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to poll status');
        }

        const data = await response.json();

        // If analysis is completed or failed, fetch full data to get scores
        if (data.status === 'completed' || data.status === 'failed') {
          const fullData = await this.fetchAnalysisById(analysisId, userId);
          return fullData;
        }

        // Otherwise, just update the status fields
        const index = this.analyses.findIndex(
          (a) => a.analysis_id === analysisId
        );
        if (index !== -1) {
          this.analyses[index] = {
            ...this.analyses[index],
            status: data.status,
            started_at: data.started_at,
            completed_at: data.completed_at,
            error_message: data.error_message,
          };
        }

        return data;
      } catch (error) {
        console.error('Error polling analysis status:', error);
        throw error;
      }
    },

    /**
     * Delete an analysis (soft delete)
     * @param {string} analysisId - Analysis UUID
     * @param {string} userId - User ID for authorization
     */
    async deleteAnalysis(analysisId, userId) {
      this.loading = true;
      this.error = null;

      try {
        const response = await fetch(
          `${config.API_BASE_URL}/analyses/${analysisId}`,
          {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
              'X-User-Id': userId,
            },
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to delete analysis');
        }

        const data = await response.json();

        // Remove from local list
        this.analyses = this.analyses.filter(
          (a) => a.analysis_id !== analysisId
        );
        this.totalAnalyses -= 1;

        this.loading = false;
        return data;
      } catch (error) {
        this.error = error.message;
        this.loading = false;
        console.error('Error deleting analysis:', error);
        throw error;
      }
    },

    /**
     * Start polling for an analysis
     * @param {string} analysisId - Analysis UUID
     * @param {string} userId - User ID
     * @param {function} onComplete - Callback when analysis completes
     */
    startPolling(analysisId, userId, onComplete) {
      // Don't start if already polling
      if (this.pollingIntervals.has(analysisId)) {
        return;
      }

      const intervalId = setInterval(async () => {
        try {
          const status = await this.pollAnalysisStatus(analysisId, userId);

          // Stop polling if completed or failed
          if (status.status === 'completed' || status.status === 'failed') {
            this.stopPolling(analysisId);
            if (onComplete) {
              onComplete(status);
            }
          }
        } catch (error) {
          console.error('Polling error:', error);
        }
      }, 5000); // Poll every 5 seconds

      this.pollingIntervals.set(analysisId, intervalId);
    },

    /**
     * Stop polling for an analysis
     * @param {string} analysisId - Analysis UUID
     */
    stopPolling(analysisId) {
      const intervalId = this.pollingIntervals.get(analysisId);
      if (intervalId) {
        clearInterval(intervalId);
        this.pollingIntervals.delete(analysisId);
      }
    },

    /**
     * Stop all active polling
     */
    stopAllPolling() {
      this.pollingIntervals.forEach((intervalId) => {
        clearInterval(intervalId);
      });
      this.pollingIntervals.clear();
    },

    /**
     * Update current page
     * @param {number} page - Page number
     */
    setCurrentPage(page) {
      this.currentPage = page;
    },

    /**
     * Reset store state
     */
    reset() {
      this.stopAllPolling();
      this.analyses = [];
      this.currentAnalysis = null;
      this.loading = false;
      this.error = null;
      this.totalAnalyses = 0;
      this.currentPage = 1;
    },
  },

  getters: {
    /**
     * Get total number of pages
     */
    totalPages: (state) => {
      return Math.ceil(state.totalAnalyses / state.limit);
    },

    /**
     * Get analyses that are currently processing
     */
    activeAnalyses: (state) => {
      return state.analyses.filter(
        (a) => a.status === 'queued' || a.status === 'processing'
      );
    },

    /**
     * Get completed analyses
     */
    completedAnalyses: (state) => {
      return state.analyses.filter((a) => a.status === 'completed');
    },

    /**
     * Get failed analyses
     */
    failedAnalyses: (state) => {
      return state.analyses.filter((a) => a.status === 'failed');
    },
  },
});
